import { useApi } from '@data-access';
import { GeneralHelper } from '@helpers';
import { Download, Loader2, Pause, Play } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import styles from './AudioPlayback.module.scss';

interface AudioPlaybackProps {
  id?: number;
  resolveRecording?: () => Promise<{ id: number; file: string }>;
}

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const getRatio = (clientX: number, rect: DOMRect) => Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

export const AudioPlayback: React.FC<AudioPlaybackProps> = ({ id, resolveRecording }) => {
  const { datasetsApi } = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const resolvedIdRef = useRef<number | null>(id ?? null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const wasPlayingRef = useRef(false);

  useEffect(
    () =>
      // Reset audio state when the source changes (e.g. switching records)
      () => {
        audioRef.current?.pause();
        audioRef.current = null;
        blobUrlRef.current = null;
        resolvedIdRef.current = id ?? null;
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(null);
      },
    [id, resolveRecording],
  );

  const attachListeners = (audio: HTMLAudioElement) => {
    const el = audio; // eslint wants a local variable, not direct param mutation
    el.onloadedmetadata = () => setDuration(el.duration);
    el.ontimeupdate = () => {
      if (!isScrubbing) setCurrentTime(el.currentTime);
    };
    el.onended = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
  };

  const handleToggle = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    if (audioRef.current) {
      await audioRef.current.play();
      setIsPlaying(true);
      return;
    }
    setIsLoading(true);
    try {
      if (!blobUrlRef.current) {
        const recording = resolveRecording
          ? await resolveRecording()
          : await datasetsApi.apiDatasetsAudioRecordingsRetrieve({ id: id! });
        blobUrlRef.current = recording.file;
        resolvedIdRef.current = recording.id;
      }
      const audio = new Audio(blobUrlRef.current);
      attachListeners(audio);
      audioRef.current = audio;
      await audio.play();
      setIsPlaying(true);
    } catch {
      toast.error('Failed to play audio recording');
    } finally {
      setIsLoading(false);
    }
  };

  const seek = (clientX: number) => {
    if (!audioRef.current || !duration || !barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const newTime = getRatio(clientX, rect) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    e.preventDefault();
    wasPlayingRef.current = isPlaying;
    audioRef.current.pause();
    setIsScrubbing(true);
    seek(e.clientX);

    const onMouseMove = (ev: MouseEvent) => seek(ev.clientX);
    const onMouseUp = () => {
      setIsScrubbing(false);
      if (wasPlayingRef.current && audioRef.current) {
        audioRef.current.play();
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      if (!blobUrlRef.current) {
        const recording = resolveRecording
          ? await resolveRecording()
          : await datasetsApi.apiDatasetsAudioRecordingsRetrieve({ id: id! });
        blobUrlRef.current = recording.file;
        resolvedIdRef.current = recording.id;
      }
      // Route through the backend /api/download/ proxy — same-origin, no CORS concerns,
      // and handles auth. Direct fetch of the S3 URL would require S3 CORS headers.
      const ext = blobUrlRef.current.split('?')[0].split('.').pop() ?? 'wav';
      await GeneralHelper.downloadFile(blobUrlRef.current, `recording-${resolvedIdRef.current}.${ext}`);
    } catch {
      toast.error('Failed to download audio recording');
    } finally {
      setIsDownloading(false);
    }
  };

  const progress = duration && duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={styles.player}>
      <button
        className={styles.iconButton}
        onClick={handleToggle}
        disabled={isLoading}
      >
        {isPlaying ? (
          <Pause
            size={12}
            fill="currentColor"
          />
        ) : (
          <Play
            size={12}
            fill="currentColor"
          />
        )}
      </button>
      <button
        className={styles.downloadButton}
        onClick={handleDownload}
        disabled={isDownloading}
        title="Download recording"
      >
        {isDownloading ? (
          <Loader2
            size={12}
            className={styles.spin}
          />
        ) : (
          <Download size={12} />
        )}
      </button>
      <div className={styles.right}>
        <div
          ref={barRef}
          className={styles.progressBar}
          onMouseDown={handleMouseDown}
          data-scrubbing={isScrubbing || undefined}
        >
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%`, transition: isScrubbing ? 'none' : undefined }}
          />
          <div
            className={styles.thumb}
            style={{ left: `${progress}%`, transition: isScrubbing ? 'none' : undefined }}
          />
        </div>
        <span className={styles.time}>
          {formatTime(currentTime)}
          {duration != null ? ` / ${formatTime(duration)}` : ' / --:--'}
        </span>
      </div>
    </div>
  );
};
